import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBedarfeSchoolCityCounts } from '../../api/bedarfe';
import type { SchoolCityCounts } from '../../types/bedarf';

type SortMode = 'alpha' | 'count';

function CityRow({ city }: { city: SchoolCityCounts }) {
  const navigate = useNavigate();
  const clickable = city.schoolCityId !== null;

  const handleClick = () => {
    if (clickable) {
      navigate(`/bedarfe?schoolCity=${city.schoolCityId}`);
    }
  };

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        disabled={!clickable}
        className={`w-full flex items-center justify-between py-3 px-2 text-left rounded-md transition-colors ${
          clickable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
        }`}
      >
        <span className="text-sm font-medium text-gray-900 truncate">
          {city.schoolCityName}
        </span>
        <span className="flex items-baseline gap-2 shrink-0">
          <span
            className="text-lg font-bold text-primary-700"
            title="Offen + Kennenlerngespräch"
          >
            {city.highlighted}
          </span>
          <span className="text-xs text-gray-400" title="Anfragen">
            ({city.anfrage})
          </span>
        </span>
      </button>
    </li>
  );
}

export function SchoolCityOverview() {
  const { data, isLoading, error } = useBedarfeSchoolCityCounts();
  const [sortMode, setSortMode] = useState<SortMode>('alpha');

  const cities = useMemo(() => {
    const list = data?.data ?? [];
    if (sortMode === 'count') {
      // "Anzahl": keep the backend order (highlighted desc, then name).
      return list;
    }
    // "A–Z": sort by city name; the catch-all bucket without a city stays last.
    return [...list].sort((a, b) => {
      if (a.schoolCityId === null) return 1;
      if (b.schoolCityId === null) return -1;
      return a.schoolCityName.localeCompare(b.schoolCityName, 'de');
    });
  }, [data?.data, sortMode]);

  return (
    <div className="bg-white rounded-lg shadow flex flex-col max-h-[70vh]">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Schulstädte – Offene Bedarfe
          </h2>
          <div
            className="shrink-0 inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50"
            role="group"
            aria-label="Sortierung"
          >
            <button
              type="button"
              onClick={() => setSortMode('alpha')}
              aria-pressed={sortMode === 'alpha'}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                sortMode === 'alpha'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              A–Z
            </button>
            <button
              type="button"
              onClick={() => setSortMode('count')}
              aria-pressed={sortMode === 'count'}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                sortMode === 'count'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Anzahl
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          <span className="font-semibold text-primary-700">Offen + Kennenlernen</span>
          <span className="ml-2">(Anfragen)</span>
        </p>
      </div>
      <div className="flex-1 overflow-y-auto px-4">
        {isLoading ? (
          <div className="py-8 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-600">
            Fehler beim Laden
          </p>
        ) : cities.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            Keine offenen Bedarfe
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {cities.map((c) => (
              <CityRow
                key={c.schoolCityId ?? '__no_city__'}
                city={c}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
